import {Category} from '../constants';

export function hasAllowedCharacters(str: string): boolean {
  const allowedChars = /^[a-zA-Z0-9\s\-?!\/[\](){};:.,%&+=<>~]*$/;
  return allowedChars.test(str);
}

export function capitalizeName(
  name: string,
  skipOnParentheses = false,
): string {
  if (skipOnParentheses) {
    return name
      .split(' ')
      .map((part) => {
        return part.startsWith('(') || part.endsWith(')')
          ? part.toLowerCase()
          : part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
      })
      .join(' ')
      .trim();
  }

  return name
    .split(' ')
    .map((part) => {
      const firstCharIsSpecialChar = part.startsWith('[');

      return firstCharIsSpecialChar
        ? part.charAt(0) +
            part.charAt(1).toUpperCase() +
            part.slice(2).toLowerCase()
        : part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
    })
    .join(' ')
    .trim();
}

export function moveFirstWordToEnd(str: string) {
  const words = str.split(' ');
  const firstWord = words.shift();
  if (firstWord) {
    words.push(firstWord);
  }
  return words.join(' ');
}

export function getCategoryInfoByEbilityCode(
  categories: Category[],
  ebilityCategoryCode: string,
): {
  webDepartmentName: string;
  mainCategoryName: string;
  subCategoryName: string;
  ebilityCategoryName: string;
  ebilityCategoryCode: string;
} | null {
  const categoryInfo = {
    webDepartmentName: '',
    mainCategoryName: '',
    subCategoryName: '',
    ebilityCategoryName: '',
    ebilityCategoryCode: '',
  };

  function addNewCategoryInfo(currentInfo: string, newInfo: string) {
    if (!newInfo) {
      return currentInfo;
    }

    if (!currentInfo) {
      return newInfo.trim();
    }

    if (
      currentInfo.toLowerCase().trim().includes(newInfo.toLowerCase().trim())
    ) {
      return currentInfo;
    }

    return newInfo.trim();
  }

  for (const category of categories) {
    for (const mainCategory of category.webDepartment.mainCategories) {
      for (const subCategory of mainCategory.subCategories) {
        const ebilityCategory = subCategory.ebilityCategories.find(
          (ec) => ec.code === ebilityCategoryCode,
        );
        if (ebilityCategory) {
          categoryInfo.webDepartmentName = addNewCategoryInfo(
            categoryInfo.webDepartmentName,
            category.webDepartment.name,
          );
          categoryInfo.mainCategoryName = addNewCategoryInfo(
            categoryInfo.mainCategoryName,
            mainCategory.name,
          );
          categoryInfo.subCategoryName = addNewCategoryInfo(
            categoryInfo.subCategoryName,
            subCategory.name,
          );
          categoryInfo.ebilityCategoryName = addNewCategoryInfo(
            categoryInfo.ebilityCategoryName,
            ebilityCategory.name,
          );
          categoryInfo.ebilityCategoryCode = addNewCategoryInfo(
            categoryInfo.ebilityCategoryCode,
            ebilityCategory.code,
          );
        }
      }
    }
  }
  if (!categoryInfo.ebilityCategoryCode) {
    return null;
  }

  return categoryInfo;
}

export function getEbilityCategoryCodesByWebDepartment(
  categories: Category[],
  webDepartmentName: string,
): string[] {
  const getEbilityCategoryCodesBySubCategory = new Set<string>();

  categories
    .filter((category) => category.webDepartment.name === webDepartmentName)
    .flatMap((category) => category.webDepartment.mainCategories)
    .flatMap((mainCategory) =>
      mainCategory.subCategories.flatMap((subCategory) =>
        subCategory.ebilityCategories.map((ebilityCategory) =>
          getEbilityCategoryCodesBySubCategory.add(ebilityCategory.code),
        ),
      ),
    );

  return Array.from(getEbilityCategoryCodesBySubCategory);
}

export function getEbilityCategoryCodesByMainCategory(
  categories: Category[],
  webDepartmentName: string,
  mainCategoryName: string,
): string[] {
  const codes = new Set<string>();

  categories
    .filter((category) => category.webDepartment.name === webDepartmentName)
    .flatMap((category) => category.webDepartment.mainCategories)
    .filter((mainCategory) => mainCategory.name === mainCategoryName)
    .flatMap((mainCategory) =>
      mainCategory.subCategories.flatMap((subCategory) =>
        subCategory.ebilityCategories.map((ebilityCategory) => {
          codes.add(ebilityCategory.code);
        }),
      ),
    );

  return Array.from(codes);
}

export function getEbilityCategoryCodesBySubCategory(
  category: Category[],
  subCategoryName: string,
): string[] {
  const codes = new Set<string>();

  category.forEach(({webDepartment}) => {
    webDepartment.mainCategories.forEach((mainCategory) => {
      mainCategory.subCategories.forEach((subCategory) => {
        if (subCategory.name === subCategoryName) {
          subCategory.ebilityCategories.forEach((ebilityCategory) => {
            codes.add(ebilityCategory.code);
          });
        }
      });
    });
  });
  return Array.from(codes);
}

export function limitStringLength(str: string, limit: number) {
  return str.length > limit ? str.substring(0, limit) + '...' : str;
}

export function stripParenthesesAndContent(str: string): string {
  return str.replace(/\([^)]*\)/g, '').trim();
}
